import { z } from 'zod';
import { pageSchema, paginationQuerySchema, timestampSchema, uuidSchema } from './common';
import { entityAnchorQuerySchema, entityKindSchema, entityRefSchema } from './entities';
import { linkedEntitySchema } from './links';

/**
 * Comments and votes on anything the platform holds
 * (docs/register-collaboration-design.md section 6).
 *
 * The wish arrived as "comments on ideas", but nothing about a comment is
 * register-shaped: the same thread is wanted on a schedule task, a cost account
 * and a map feature. So it is keyed by `(entityKind, entityId)` like the
 * revisions beside it, and the register is its first consumer rather than its
 * owner.
 *
 * Which kinds take comments, and which take votes, is declared in
 * `ENTITY_CATALOG` -- see `EntityCollaboration`. Nothing here switches on kind.
 */

/* --------------------------------------------------------------- comments */

/**
 * Long enough for a paragraph of reasoning, short enough that nobody writes the
 * minutes in it. A meeting agenda body is 20,000 characters because it *is* the
 * record; a comment is a remark about one, and a comment box the size of a
 * document invites people to put the document in the wrong place.
 */
export const MAX_COMMENT_LENGTH = 4000;

export const entityCommentSchema = z.object({
  id: uuidSchema,
  entityKind: entityKindSchema,
  entityId: uuidSchema,
  /** Null for a top-level comment. One level only -- see `createCommentSchema`. */
  parentId: uuidSchema.nullable(),
  /**
   * The Markdown subset, unrendered. Null on a withdrawn comment that had
   * replies: the row survives so the thread does, and its text does not.
   */
  body: z.string().nullable(),
  authorUserId: uuidSchema.nullable(),
  /** Denormalized at write time, for the reason `EntityRevision.actorLabel`
   *  gives: a thread of "«unknown» said" is not a thread anybody can follow. */
  authorLabel: z.string(),
  /** Set when the author has changed the text since posting it. The panel says
   *  "edited"; what it used to say is not kept, because a comment is not the
   *  record (section 4.6 of the design). */
  editedAt: timestampSchema.nullable(),
  /** Set on a withdrawn comment whose replies keep it in the thread. */
  deletedAt: timestampSchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
});
export type EntityComment = z.infer<typeof entityCommentSchema>;

export const entityCommentPageSchema = pageSchema(entityCommentSchema);

export const commentListQuerySchema = paginationQuerySchema
  .extend(entityAnchorQuerySchema.shape)
  .strict();
export type CommentListQuery = z.infer<typeof commentListQuerySchema>;

/**
 * Posting one.
 *
 * `parentId` may name a top-level comment and nothing deeper. The rule is
 * enforced in the service, which is the only layer that can see whether a
 * proposed parent is itself a reply -- the same place, and the same reasoning,
 * as the meeting agenda's one-level nesting rule.
 */
export const createCommentSchema = z
  .object({
    anchor: entityRefSchema,
    body: z.string().min(1).max(MAX_COMMENT_LENGTH),
    parentId: uuidSchema.nullish(),
  })
  .strict();
export type CreateComment = z.infer<typeof createCommentSchema>;

/**
 * Editing one: the text, and only by its author.
 *
 * Versioned like everything else that can be edited from two tabs. There is no
 * `parentId` here on purpose -- moving a comment to a different parent is not
 * an edit of the comment, it is a different comment.
 */
export const updateCommentSchema = z
  .object({
    body: z.string().min(1).max(MAX_COMMENT_LENGTH),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateComment = z.infer<typeof updateCommentSchema>;

/* ------------------------------------------------------------------ votes */

/**
 * Two values, and abstention is the absence of a row.
 *
 * A five-point scale repeats the mistake `register.ts` already warns about --
 * "a register whose scale has ten is a register whose entries are all sixes and
 * sevens" -- and an explicit `neutral` records something nobody meant to say.
 * Not having voted is the truthful representation of not having voted.
 *
 * This is a tally, not a ballot. There is no eligibility, no quorum and no
 * closing date, because those belong to a governance product and not to a list
 * of ideas somebody wants ordered by support.
 */
export const VOTE_VALUES = ['support', 'oppose'] as const;
export const voteValueSchema = z.enum(VOTE_VALUES);
export type VoteValue = (typeof VOTE_VALUES)[number];

/**
 * What a vote control needs to draw itself: the two counts, and the reader's
 * own position.
 *
 * Who voted which way is deliberately not here. It is a small change to add and
 * a large social one -- a named tally is a different instrument from an
 * anonymous one, and which the client wants is question 3 in the design's
 * section 11.
 */
export const voteTallySchema = z.object({
  entityKind: entityKindSchema,
  entityId: uuidSchema,
  support: z.number().int().nonnegative(),
  oppose: z.number().int().nonnegative(),
  /** The caller's own vote, or null if they have not cast one. */
  mine: voteValueSchema.nullable(),
});
export type VoteTally = z.infer<typeof voteTallySchema>;

/**
 * Casting one.
 *
 * `PUT .../votes/me` rather than `POST .../votes`: one row per person is the
 * invariant, so the resource is "my vote", and casting it twice is idempotent
 * rather than a duplicate the unique index has to refuse.
 */
export const castVoteSchema = z
  .object({ anchor: entityRefSchema, value: voteValueSchema })
  .strict();
export type CastVote = z.infer<typeof castVoteSchema>;

/** Reading a tally, and withdrawing a vote: the anchor and nothing else. */
export const voteQuerySchema = entityAnchorQuerySchema.strict();
export type VoteQuery = z.infer<typeof voteQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Recent activity                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The most recent conversation across everything the caller may read.
 *
 * Every other read here is anchored: you ask about one item and get its thread.
 * This one is the opposite question -- "has anybody said anything?" -- which is
 * what the `discussion-activity` dashboard tile is for, and which cannot be
 * answered by asking each kind in turn from the browser.
 *
 * The target is a resolved `LinkedEntity` rather than a bare `(kind, id)` pair
 * for the reason the link list resolves its own: a feed saying "somebody
 * commented on 4f3c-…" is not a feed. It carries the `href` too, so the tile
 * links through without knowing how another module routes.
 */
export const recentCommentSchema = z.object({
  id: uuidSchema,
  /** What was commented on, resolved and permission-checked by the API. */
  target: linkedEntitySchema,
  authorLabel: z.string(),
  /**
   * The opening of the comment, not the whole of it.
   *
   * Trimmed server-side rather than by the tile's CSS, because a four-thousand
   * character body sent to a dashboard so that a `text-overflow` rule can hide
   * it is four thousand characters of somebody's private-ish remark on the
   * wire for no reason. The Markdown is not rendered here: an excerpt is read
   * as one line of plain text, and running the sanitizer over a fragment cut
   * mid-syntax is how a stray `<` becomes a rendering bug.
   */
  excerpt: z.string(),
  createdAt: timestampSchema,
});
export type RecentComment = z.infer<typeof recentCommentSchema>;

export const recentCommentListSchema = z.object({ items: z.array(recentCommentSchema) });
export type RecentCommentList = z.infer<typeof recentCommentListSchema>;

/** How much of a comment reaches the feed. A line, not a paragraph. */
export const RECENT_COMMENT_EXCERPT_LENGTH = 140;

export const recentCommentQuerySchema = z
  .object({
    /**
     * Bounded well below the tile's own ceiling would need, because this is the
     * one discussion read that is not anchored: it scans across kinds, and an
     * unbounded version would be the most expensive tile on a dashboard.
     */
    limit: z.coerce.number().int().min(1).max(20).default(5),
  })
  .strict();
export type RecentCommentQuery = z.infer<typeof recentCommentQuerySchema>;
